import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { SEOLink } from './SEOLink';

// Enhanced breadcrumb name mapping
const breadcrumbNameMap = {
  'about': 'About Us',
  'services': 'Services',
  'products': 'Pricing',
  'pricing': 'Pricing',
  'success-stories': 'Success Stories',
  'blog': 'Blog',
  'contact': 'Contact',
  'team': 'Our Team',
  'life-at-zaroori': 'Life at Zaroori',
  'privacy-policy': 'Privacy Policy',
  'terms-of-service': 'Terms of Service',
  'refund-policy': 'Refund Policy',
  'sitemap': 'Sitemap',
  'admin': 'Admin',
  'jinistudio': 'JiniStudio',
  // Marketplace pages
  'myntra': 'Myntra Onboarding',
  'nykaa': 'Nykaa Onboarding',
  'ajio': 'Ajio Onboarding',
  'tata-cliq': 'Tata Cliq Onboarding',
  'tata-cliq-luxury': 'Tata Cliq Luxury',
  'tira': 'Tira Beauty',
  'blinkit': 'Blinkit',
  'zepto': 'Zepto',
  // SEO Pages
  'myntra-seller-account-management': 'Myntra Seller Guide',
  'nykaa-seller-onboarding-guide': 'Nykaa Seller Guide',
  'ajio-seller-onboarding-guide': 'Ajio Seller Guide',
  'tira-seller-onboarding-guide': 'Tira Seller Guide',
  'pernias-pop-up-shop-seller-guide': "Pernia's Pop-Up Shop Guide",
  'global-marketplace-seller-guides': 'Global Marketplace Guides',
  'sell-on-india-marketplaces': 'Sell on India Marketplaces',
  'sell-on-us-marketplaces': 'Sell on US Marketplaces',
  'sell-on-uae-marketplaces': 'Sell on UAE Marketplaces',
  'deepinder-ahluwalia': 'Deepinder Ahluwalia'
};

// Generate BreadcrumbList Schema
export const generateBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

// Enhanced Breadcrumbs Component with Schema
const EnhancedBreadcrumbs = ({ customItems, className = '' }) => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  // Skip rendering on homepage
  if (pathnames.length === 0) return null;

  // Build breadcrumb items
  let breadcrumbItems = [
    { name: 'Home', url: 'https://zarooriretail.com' }
  ];

  if (customItems) {
    // Use custom items if provided
    breadcrumbItems = [...breadcrumbItems, ...customItems];
  } else {
    // Auto-generate from path
    pathnames.forEach((value, index) => {
      const url = `https://zarooriretail.com/${pathnames.slice(0, index + 1).join('/')}`;
      const name = breadcrumbNameMap[value] || value.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      breadcrumbItems.push({ name, url });
    });
  }

  return (
    <>
      {/* BreadcrumbList Schema */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(generateBreadcrumbSchema(breadcrumbItems))}
        </script>
      </Helmet>

      {/* Visual Breadcrumb Navigation */}
      <nav 
        className={`bg-gray-50 py-3 border-b border-gray-100 ${className}`} 
        aria-label="Breadcrumb"
        data-testid="breadcrumb-nav"
      >
        <div className="max-w-7xl mx-auto px-4">
          <ol className="flex items-center gap-2 text-sm flex-wrap" itemScope itemType="https://schema.org/BreadcrumbList">
            {breadcrumbItems.map((item, index) => {
              const isLast = index === breadcrumbItems.length - 1;
              const isHome = index === 0;

              return (
                <li 
                  key={item.url} 
                  className="flex items-center gap-2"
                  itemScope
                  itemType="https://schema.org/ListItem"
                  itemProp="itemListElement"
                >
                  {index > 0 && (
                    <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
                  )}
                  
                  {isLast ? (
                    <span 
                      className="text-red-500 font-medium truncate max-w-[200px]"
                      itemProp="name"
                    >
                      {item.name}
                    </span>
                  ) : (
                    <SEOLink 
                      to={isHome ? '/' : item.url.replace('https://zarooriretail.com', '')}
                      className="text-gray-500 hover:text-red-500 transition-colors flex items-center gap-1"
                      itemProp="item"
                    >
                      {isHome && <Home className="w-4 h-4" />}
                      <span itemProp="name">{item.name}</span>
                    </SEOLink>
                  )}
                  <meta itemProp="position" content={index + 1} />
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </>
  );
};

export default EnhancedBreadcrumbs;
